import React, { useEffect, useRef, useState } from "react";
import api, { API, formatApiErrorDetail } from "../lib/api";
import { Upload, Trash, FilePdf, Image as ImageIcon, X } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function PatientAttachments({ patientId, readOnly }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef();

  const load = async () => setItems((await api.get(`/patients/${patientId}/attachments`)).data);
  useEffect(() => { load(); }, [patientId]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "xray");
      await api.post(`/patients/${patientId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this file?")) return;
    await api.delete(`/attachments/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="attachments-section">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.25em] font-black text-zinc-700">X-rays & photos</div>
        {!readOnly && (
          <label className="text-xs uppercase tracking-[0.2em] font-bold text-[#E60000] inline-flex items-center gap-1 cursor-pointer">
            <Upload size={14} weight="bold"/> {busy ? "Uploading…" : "Upload"}
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden"
              onChange={(e)=>upload(e.target.files?.[0])} disabled={busy} data-testid="attachment-upload-input"/>
          </label>
        )}
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-zinc-500 bg-zinc-50 border border-dashed border-zinc-200 rounded-sm p-6 text-center">No files yet.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {items.map((a) => (
            <AttachmentTile key={a.id} a={a} onOpen={()=>setPreview(a)} onDelete={readOnly?null:()=>remove(a.id)}/>
          ))}
        </div>
      )}

      {preview && <PreviewModal a={preview} onClose={()=>setPreview(null)}/>}
    </div>
  );
}

function AttachmentTile({ a, onOpen, onDelete }) {
  const isImg = a.content_type?.startsWith("image/");
  const isPdf = a.content_type === "application/pdf";
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    if (!isImg) return;
    let url;
    api.get(`/attachments/${a.id}/download`, { responseType: "blob" }).then(r => {
      url = URL.createObjectURL(r.data);
      setBlobUrl(url);
    });
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [a.id, isImg]);

  return (
    <div className="relative group bg-white border border-zinc-200 rounded-sm overflow-hidden">
      <button onClick={onOpen} className="block w-full" data-testid={`attachment-${a.id}`}>
        <div className="aspect-square bg-zinc-100 flex items-center justify-center overflow-hidden">
          {isImg && blobUrl ? <img src={blobUrl} alt={a.original_filename} className="w-full h-full object-cover"/> :
           isPdf ? <FilePdf size={48} weight="duotone" color="#E60000"/> :
           <ImageIcon size={48} weight="duotone" color="#999"/>}
        </div>
        <div className="px-2 py-2 text-left">
          <div className="text-xs font-bold text-zinc-900 truncate">{a.original_filename}</div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">{(a.size/1024).toFixed(0)} KB · {a.kind}</div>
        </div>
      </button>
      {onDelete && (
        <button onClick={onDelete} className="absolute top-1 right-1 bg-white/90 border border-zinc-200 rounded-sm p-1 text-zinc-500 hover:text-[#E60000] opacity-0 group-hover:opacity-100 transition-opacity"
          data-testid={`attachment-delete-${a.id}`}>
          <Trash size={14} weight="bold"/>
        </button>
      )}
    </div>
  );
}

function PreviewModal({ a, onClose }) {
  const isImg = a.content_type?.startsWith("image/");
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    let url;
    api.get(`/attachments/${a.id}/download`, { responseType: "blob" }).then(r => {
      url = URL.createObjectURL(r.data);
      setBlobUrl(url);
    });
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [a.id]);
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" onClick={onClose}>
      <div onClick={(e)=>e.stopPropagation()} className="bg-white max-w-5xl w-full max-h-[90vh] rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
          <div className="text-sm font-bold truncate">{a.original_filename}</div>
          <button onClick={onClose}><X size={20}/></button>
        </div>
        <div className="bg-zinc-100 flex items-center justify-center" style={{maxHeight:"80vh", overflow:"auto"}}>
          {blobUrl ? (isImg ? <img src={blobUrl} alt="" className="max-w-full max-h-[80vh]"/> :
            <iframe src={blobUrl} title="preview" className="w-full" style={{height:"80vh", border:0}}/>) :
            <div className="p-12 text-sm text-zinc-500">Loading…</div>}
        </div>
      </div>
    </div>
  );
}
